import { memoizeGetUserDetails, getUserDetails } from "./users";
import { fetchClassroomCourses } from "./courses";
import {
  getClassroom,
  updateClassroom as updateClassroomAPI,
} from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { getImageUrl, getImageUrlWithSize } from "../imageDownload";
import {
  invalidateCache,
  DataType,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import { getClassroomEnrolmentKey } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import config from "../../config";

import logger from "../../logger";

export const formatMember = async (m, bypassCache) => {
  let userDetails;
  if (bypassCache) {
    userDetails = await getUserDetails(m.memberId, true);
  } else {
    userDetails = await memoizeGetUserDetails(m.memberId);
  }
  const userImage =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.picture
      : null;
  const username =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.preferred_username
      : null;
  const gameAward = userDetails && userDetails ? userDetails.gameAward : null;
  return {
    ...m,
    userImage,
    username,
    gameAward,
  };
};

export const formatMembers = (members = [], bypassCache) =>
  Promise.all(members.map((member) => formatMember(member, bypassCache)));

export const formatClassroom = async (
  c,
  verbose = true,
  user,
  ignoreKey = false,
) => {
  const { username, attributes } = user;
  const isCreator =
    c.createdBy === username ||
    (c.collaborators &&
      Array.isArray(c.collaborators) &&
      // A collaborator entry may be an email (case-insensitive) or a Cognito
      // username/userId (exact match — usernames are case-sensitive).
      c.collaborators.some(
        (collaborator) =>
          collaborator.toLowerCase() === attributes?.email?.toLowerCase() ||
          collaborator === username,
      ));
  if (isCreator && !ignoreKey) {
    // Enrolment key only exists if classroom is private and should only be accessible by creator or collaborator
    try {
      const enrolmentResponse = await getClassroomEnrolmentKey(c.classroomId);
      if (enrolmentResponse?.Item?.enrolmentKey) {
        c.enrolmentKey = enrolmentResponse.Item.enrolmentKey || "";
      }
    } catch (err) {
      logger.error(err);
    }
  }
  const { courses } = c;
  const responses = await Promise.all([
    // Creator details are only used for the name/avatar, so a failed lookup
    // must not stop the classroom from loading.
    memoizeGetUserDetails(c.createdBy).catch((err) => {
      logger.error(`Error getting classroom creator details: ${err}`);
      return null;
    }),
    verbose ? fetchClassroomCourses(courses, user) : courses,
  ]);
  const userDetails = responses[0];
  const formattedCourses = responses[1];
  const userImage =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.picture
      : null;
  const verificationStatus =
    userDetails && userDetails.verificationStatus
      ? userDetails.verificationStatus
      : null;
  const gameAward =
    userDetails && userDetails.gameAward ? userDetails.gameAward : null;
  const createdByName =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.preferred_username
      : null;
  let pictureXs = null;
  let cardPicture = null;
  let bannerXs = null;
  if (c.pictureS3Key) {
    pictureXs = getImageUrl(c.pictureS3Key, {
      resize: {
        width: 25,
      },
    });
    cardPicture = getImageUrl(c.pictureS3Key, {
      resize: {
        width: 600,
      },
    });
  } else if (
    c.picture &&
    c.picture.includes("nualang.com/") &&
    c.picture.includes("images")
  ) {
    pictureXs = getImageUrlWithSize(c.picture, null, 25);
    cardPicture = getImageUrlWithSize(c.picture, null, 600);
  }

  if (c.picture && c.picture.includes(config.OldImageCreatorURL)) {
    const path = c.picture.split(config.OldImageCreatorURL)[1];
    c.picture = `${config.API}/image-creator${path}`;
  }

  if (c.bannerS3Key) {
    bannerXs = getImageUrl(c.bannerS3Key, {
      resize: {
        width: 25,
      },
    });
  } else if (
    c.banner &&
    c.banner.includes("nualang.com/") &&
    c.banner.includes("images")
  ) {
    bannerXs = getImageUrlWithSize(c.banner, null, 25);
  }

  return {
    ...c,
    isCreator,
    userImage,
    verificationStatus,
    gameAward,
    createdByName,
    courses: formattedCourses,
    pictureXs,
    cardPicture,
    bannerXs,
  };
};

export const formatClassrooms = async (classrooms = [], verbose, user) =>
  await Promise.all(
    classrooms.map((c) => formatClassroom(c, verbose, user, true)),
  );

export const addCourseToClassroom = async (courseId, classroomId) => {
  const response = await getClassroom(classroomId);
  const { courses = [] } = response.Item;
  let result;
  if (courses.findIndex((cId) => cId === courseId) < 0) {
    result = await updateClassroomAPI(classroomId, {
      courses: [...courses, courseId],
    });
  }
  await invalidateCache(DataType.CLASSROOM, {
    classroomId,
  });
  return result;
};

export const addCourseToClassrooms = async (courseId, classroomIds) => {
  try {
    const updateCalls = (classroomIds || []).map((cId) => {
      return addCourseToClassroom(courseId, cId);
    });

    await Promise.all(updateCalls);
  } catch (error) {
    logger.error(error);
  }
};

export const DEFAULT_FILTERS = {
  visibility: "all",
  lang: "all",
  sort: "newest",
  search: "",
};

export function isDefaultFilters(filters = {}) {
  return Object.keys(DEFAULT_FILTERS).every(
    (key) => filters[key] === DEFAULT_FILTERS[key],
  );
}

function getClassroomCreatedAt(classroom) {
  return classroom?.createdAt || 0;
}

export function filterAndSortClassrooms(classrooms = [], filters = {}) {
  const {
    visibility = DEFAULT_FILTERS.visibility,
    lang = DEFAULT_FILTERS.lang,
    sort = DEFAULT_FILTERS.sort,
    search = DEFAULT_FILTERS.search,
  } = filters;
  const searchText = search.trim().toLowerCase();

  const filtered = classrooms.filter((classroom) => {
    if (visibility !== "all" && classroom?.visibility !== visibility) return false;
    if (lang !== "all" && classroom?.learnLang !== lang) return false;
    if (searchText) {
      const nameMatch = (classroom?.classroomName || "")
        .toLowerCase()
        .includes(searchText);
      const descriptionMatch = (classroom?.description || "")
        .toLowerCase()
        .includes(searchText);
      if (!nameMatch && !descriptionMatch) return false;
    }
    return true;
  });

  const byNameAsc = (a, b) =>
    (a?.classroomName || "").localeCompare(b?.classroomName || "", undefined, {
      sensitivity: "base",
    });

  const sorted = [...filtered];
  if (sort === "oldest") {
    sorted.sort(
      (a, b) =>
        getClassroomCreatedAt(a) - getClassroomCreatedAt(b) || byNameAsc(a, b),
    );
  } else if (sort === "alphabetical") {
    sorted.sort(byNameAsc);
  } else {
    sorted.sort(
      (a, b) =>
        getClassroomCreatedAt(b) - getClassroomCreatedAt(a) || byNameAsc(a, b),
    );
  }

  return sorted;
}
